
import React, { useEffect, useRef, useState } from 'react';
import { Upload, Loader2, Image as ImageIcon, ScanEye, Search, Sparkles, Target, AlertCircle } from 'lucide-react';
import { chatApi } from '../lib/api';
import AiTaskProgressOverlay from './AiTaskProgressOverlay';
import XuanYuanTopBar from './XuanYuanTopBar';

interface FindObjectProps {
  onBack: () => void;
  intro?: string;
}

interface FoundObject {
  objectName: string;
  location: string;
  matchReason: string;
  similarity: number;
}

interface AnalysisResult {
  analysis: string;
  objects: FoundObject[];
}

const GODS = ['', '值符', '螣蛇', '太阴', '六合', '白虎', '玄武', '九地', '九天'];
const STARS = ['', '天蓬', '天任', '天冲', '天辅', '天英', '天芮', '天柱', '天心', '天禽'];
const DOORS = ['', '休门', '生门', '伤门', '杜门', '景门', '死门', '惊门', '开门'];
const FIND_OBJECT_LOADING_STEPS = ['分析现场图像', '匹配象意候选', '计算相似度排序', '生成定位建议'];
const FIND_OBJECT_LOADING_DURATION_MS = 45000;
const LOADING_PROGRESS_TICK_MS = 100;
const LOADING_PROGRESS_MAX_PERCENT = 96;

function extractJsonText(raw: string): string {
  const text = String(raw || '')
    .replace(/^\uFEFF/, '')
    .replace(/^json\s*/i, '')
    .trim();
  if (!text) return text;

  const codeBlockMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (codeBlockMatch?.[1]) return codeBlockMatch[1].trim();

  const startObj = text.indexOf('{');
  const startArr = text.indexOf('[');
  let start = -1;
  if (startObj >= 0 && startArr >= 0) start = Math.min(startObj, startArr);
  else start = Math.max(startObj, startArr);
  if (start < 0) return text;

  const open = text[start];
  const close = open === '{' ? '}' : ']';
  let depth = 0;
  let inStr = false;
  let escaped = false;

  for (let i = start; i < text.length; i += 1) {
    const ch = text[i];
    if (inStr) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (ch === '\\') {
        escaped = true;
        continue;
      }
      if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') {
      inStr = true;
      continue;
    }
    if (ch === open) depth += 1;
    if (ch === close) {
      depth -= 1;
      if (depth === 0) return text.slice(start, i + 1).trim();
    }
  }
  return text.slice(start).trim();
}

function parseAnalysisResult(raw: string): AnalysisResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(extractJsonText(raw));
  } catch {
    throw new Error('返回格式异常，请重试');
  }
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(extractJsonText(parsed));
    } catch {
      throw new Error('返回格式异常，请重试');
    }
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('返回格式异常，请重试');
  }
  const obj = parsed as Record<string, unknown>;
  const objects = Array.isArray(obj.objects)
    ? obj.objects.map((item) => {
        const o = (item && typeof item === 'object') ? item as Record<string, unknown> : {};
        const similarityRaw = Number(o.similarity);
        return {
          objectName: String(o.objectName ?? o.name ?? ''),
          location: String(o.location ?? ''),
          matchReason: String(o.matchReason ?? o.reasoning ?? ''),
          similarity: Number.isFinite(similarityRaw) ? similarityRaw : 0,
        } as FoundObject;
      }).filter((x) => x.objectName && x.location)
    : [];
  if (objects.length === 0) {
    throw new Error('未找到可用匹配结果，请更换图片或符号后重试');
  }
  return {
    analysis: String(obj.analysis ?? obj.summary ?? ''),
    objects,
  };
}

const loadImageDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('读取图片失败'));
  reader.onload = () => resolve(String(reader.result || ''));
  reader.readAsDataURL(file);
});

const compressImageDataUrl = async (
  dataUrl: string,
  options?: { maxSide?: number; targetLength?: number },
): Promise<string> => {
  const maxSide = options?.maxSide ?? 1024;
  const targetLength = options?.targetLength ?? 110000;

  const img = new Image();
  const loaded = new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('图片解析失败'));
  });
  img.src = dataUrl;
  await loaded;

  const baseScale = Math.min(1, maxSide / Math.max(img.width, img.height));
  const scales = [1, 0.92, 0.84, 0.76, 0.68];
  const qualities = [0.78, 0.68, 0.58, 0.5, 0.42];

  let best = dataUrl;
  for (const scaleMul of scales) {
    const width = Math.max(1, Math.round(img.width * baseScale * scaleMul));
    const height = Math.max(1, Math.round(img.height * baseScale * scaleMul));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) continue;
    ctx.drawImage(img, 0, 0, width, height);

    for (const quality of qualities) {
      const next = canvas.toDataURL('image/jpeg', quality);
      if (next.length < best.length) best = next;
      if (next.length <= targetLength) return next;
    }
  }
  return best;
};

const FindObject: React.FC<FindObjectProps> = ({ onBack, intro }) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [formData, setFormData] = useState({ 
    god: '', 
    star: '', 
    door: '', 
    heaven: '', 
    earth: '',
    leadStem: '',
  });
  const [attributes, setAttributes] = useState({
    isVoid: false,
    doorPo: false,
    heavenXing: false,
    heavenMu: false,
    earthXing: false,
    earthMu: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgressPercent, setLoadingProgressPercent] = useState(0);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const asOn = (key: string): boolean => ['1', 'true', 'yes'].includes((q.get(key) || '').toLowerCase());
    const nextGod = q.get('god') || '';
    const nextStar = q.get('star') || '';
    const nextDoor = q.get('door') || '';
    const nextHeaven = q.get('heavenStem') || q.get('heaven') || '';
    const nextEarth = q.get('earthStem') || q.get('earth') || '';
    const nextLead = q.get('leadStem') || '';

    setFormData((prev) => ({
      ...prev,
      god: GODS.includes(nextGod) ? nextGod : prev.god,
      star: STARS.includes(nextStar) ? nextStar : prev.star,
      door: DOORS.includes(nextDoor) ? nextDoor : prev.door,
      heaven: nextHeaven || prev.heaven,
      earth: nextEarth || prev.earth,
      leadStem: nextLead || prev.leadStem,
    }));
    setAttributes((prev) => ({
      ...prev,
      isVoid: asOn('void') || prev.isVoid,
      doorPo: asOn('doorPo') || prev.doorPo,
      heavenXing: asOn('heavenXing') || prev.heavenXing,
      heavenMu: asOn('heavenMu') || prev.heavenMu,
      earthXing: asOn('earthXing') || prev.earthXing,
      earthMu: asOn('earthMu') || prev.earthMu,
    }));
  }, []);

  useEffect(() => {
    if (!isLoading) {
      setLoadingProgressPercent(0);
      setLoadingStepIndex(0);
      return;
    }
    const startedAt = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startedAt;
      const rawPercent = (elapsed / FIND_OBJECT_LOADING_DURATION_MS) * 100;
      const nextPercent = Math.max(0, Math.min(LOADING_PROGRESS_MAX_PERCENT, rawPercent));
      const ratio = nextPercent / 100;
      const nextStep = Math.min(
        FIND_OBJECT_LOADING_STEPS.length - 1,
        Math.floor(ratio * FIND_OBJECT_LOADING_STEPS.length),
      );
      setLoadingProgressPercent(nextPercent);
      setLoadingStepIndex(nextStep);
    };
    tick();
    const timer = window.setInterval(tick, LOADING_PROGRESS_TICK_MS);
    return () => window.clearInterval(timer);
  }, [isLoading]);

  const handleAnalyze = async () => {
    if (!selectedImage) return;
    
    const hasSymbols = Object.values(formData).some((val) => (val as string).trim() !== '');
    if (!hasSymbols) {
        alert("请输入至少一个奇门符号（如：值符、天蓬、生门等）");
        return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      if (selectedImage.length > 130000) {
        throw new Error('图片仍然偏大，请换一张更清晰但范围更小的图片再试');
      }

      const formatStemWithTags = (stem: string, xing: boolean, mu: boolean): string => {
        const tags: string[] = [];
        if (xing) tags.push('击刑');
        if (mu) tags.push('入墓');
        return `${stem || '-'}${tags.length ? `（${tags.join('、')}）` : ''}`;
      };

      const prompt = [
        '【输入】查找象意',
        `八神：${formData.god || '-'}`,
        `九星：${formData.star || '-'}`,
        `八门：${formData.door || '-'}${attributes.doorPo ? '（门迫）' : ''}`,
        `天盘干：${formatStemWithTags(formData.heaven, attributes.heavenXing, attributes.heavenMu)}`,
        `地盘干：${formatStemWithTags(formData.earth, attributes.earthXing, attributes.earthMu)}`,
        `引干：${formData.leadStem || '-'}`,
        `空亡：${attributes.isVoid ? '是' : '否'}`,
      ].join('\n');

      const content = [
        { type: 'image_url' as const, image_url: { url: selectedImage } },
        { type: 'text' as const, text: prompt },
      ];

      const { text } = await chatApi({
        messages: [{ role: 'user', content }],
        jsonMode: true,
        feature: 'findObject',
        knowledgeQuery: [
          formData.god,
          formData.star,
          formData.door,
          formData.heaven,
          formData.earth,
          formData.leadStem,
          attributes.doorPo ? '门迫' : '',
          attributes.heavenXing ? '击刑' : '',
          attributes.heavenMu ? '入墓' : '',
          attributes.earthXing ? '击刑' : '',
          attributes.earthMu ? '入墓' : '',
          attributes.isVoid ? '空亡' : '',
        ].filter(Boolean).join(' '),
      });

      if (text) {
        const parsed = parseAnalysisResult(text);
        parsed.objects.sort((a: FoundObject, b: FoundObject) => b.similarity - a.similarity);
        setResult(parsed);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '查找分析失败，请检查网络或重试。';
      alert(msg === '会员已过期' ? '会员已过期，请重新激活。' : msg);
    } finally {
      setIsLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'bg-emerald-500 text-white';
    if (score >= 75) return 'bg-blue-500 text-white';
    if (score >= 60) return 'bg-amber-500 text-white';
    return 'bg-slate-400 text-white';
  };

  const AttributeBadge = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-1 text-[10px] rounded border ${active ? 'bg-red-50 border-red-200 text-red-600 font-bold' : 'bg-slate-50 border-slate-200 text-slate-400'}`}
    >
      {label}
    </button>
  );
  const ToggleSwitch = ({ checked, onChange }: { checked: boolean; onChange: () => void }) => (
    <button
      type="button"
      onClick={onChange}
      className={`relative h-6 w-11 rounded-full transition-colors ${checked ? 'bg-fuchsia-600' : 'bg-slate-300'}`}
      aria-label="切换空亡"
    >
      <span
        className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );

  return (
    <div className="h-full min-h-0 bg-slate-50 flex flex-col">
      <XuanYuanTopBar
        title="轩辕国学·查找象意"
        onBack={onBack}
        subtitle={intro}
        variant="light"
      />

      <div className="flex-1 overflow-y-auto p-4 lg:p-8">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-8">
            
            <div className="lg:col-span-5 space-y-6">
                
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-blue-500"/> 现场环境图
                    </h3>
                    <div onClick={() => fileInputRef.current?.click()} className="group border-2 border-dashed border-slate-300 h-56 rounded-xl flex flex-col items-center justify-center cursor-pointer bg-slate-50 hover:bg-blue-50 hover:border-blue-300 transition-all overflow-hidden relative">
                        {selectedImage ? (
                            <>
                                <img src={selectedImage} className="h-full w-full object-contain z-10"/>
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20">
                                    <p className="text-white font-bold flex items-center gap-2"><Upload className="w-4 h-4"/> 更换图片</p>
                                </div>
                            </>
                        ) : (
                            <div className="text-center p-4">
                                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm mx-auto mb-3 text-blue-500">
                                    <Upload className="w-6 h-6"/>
                                </div>
                                <p className="text-sm text-slate-500 font-medium">点击上传环境照片</p>
                                <p className="text-xs text-slate-400 mt-1">支持 JPG, PNG 格式</p>
                            </div>
                        )}
                        <input
                          type="file"
                          ref={fileInputRef}
                          className="hidden"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const raw = await loadImageDataUrl(file);
                              const compressed = await compressImageDataUrl(raw, { maxSide: 1024, targetLength: 110000 });
                              setSelectedImage(compressed || raw);
                            } catch {
                              alert('图片处理失败，请重试');
                            }
                          }}
                        />
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                        <Target className="w-4 h-4 text-fuchsia-500"/> 锁定宫位符号
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <label className="text-xs font-bold text-slate-400 uppercase ml-1">八神</label>
                            <select
                              value={formData.god}
                              onChange={e => setFormData({...formData, god: e.target.value})}
                              className="w-full p-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-fuchsia-500 outline-none bg-slate-50 focus:bg-white transition-all"
                            >
                              {GODS.map(g => <option key={g} value={g}>{g || '选择八神'}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-400 uppercase ml-1">九星</label>
                            <select
                              value={formData.star}
                              onChange={e => setFormData({...formData, star: e.target.value})}
                              className="w-full p-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-fuchsia-500 outline-none bg-slate-50 focus:bg-white transition-all"
                            >
                              {STARS.map(s => <option key={s} value={s}>{s || '选择九星'}</option>)}
                            </select>
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                              <label className="text-xs font-bold text-slate-400 uppercase ml-1">八门</label>
                              <AttributeBadge
                                label="门迫"
                                active={attributes.doorPo}
                                onClick={() => setAttributes((prev) => ({ ...prev, doorPo: !prev.doorPo }))}
                              />
                            </div>
                            <select
                              value={formData.door}
                              onChange={e => setFormData({...formData, door: e.target.value})}
                              className="w-full p-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-fuchsia-500 outline-none bg-slate-50 focus:bg-white transition-all"
                            >
                              {DOORS.map(d => <option key={d} value={d}>{d || '选择八门'}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-400 uppercase ml-1">天盘干</label>
                            <div className="mt-1 flex items-center gap-1">
                              <input
                                value={formData.heaven}
                                onChange={e => setFormData({...formData, heaven: e.target.value})}
                                className="w-16 max-w-[4rem] p-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-fuchsia-500 outline-none bg-slate-50 focus:bg-white transition-all"
                                placeholder="天盘干"
                              />
                              <span className="flex gap-1">
                                <AttributeBadge
                                  label="击刑"
                                  active={attributes.heavenXing}
                                  onClick={() => setAttributes((prev) => ({ ...prev, heavenXing: !prev.heavenXing }))}
                                />
                                <AttributeBadge
                                  label="入墓"
                                  active={attributes.heavenMu}
                                  onClick={() => setAttributes((prev) => ({ ...prev, heavenMu: !prev.heavenMu }))}
                                />
                              </span>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-400 uppercase ml-1">地盘干</label>
                            <div className="mt-1 flex items-center gap-1">
                              <input
                                value={formData.earth}
                                onChange={e => setFormData({...formData, earth: e.target.value})}
                                className="w-16 max-w-[4rem] p-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-fuchsia-500 outline-none bg-slate-50 focus:bg-white transition-all"
                                placeholder="地盘干"
                              />
                              <span className="flex gap-1">
                                <AttributeBadge
                                  label="击刑"
                                  active={attributes.earthXing}
                                  onClick={() => setAttributes((prev) => ({ ...prev, earthXing: !prev.earthXing }))}
                                />
                                <AttributeBadge
                                  label="入墓"
                                  active={attributes.earthMu}
                                  onClick={() => setAttributes((prev) => ({ ...prev, earthMu: !prev.earthMu }))}
                                />
                              </span>
                            </div>
                        </div>
                        <div className="col-span-2 flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-400 uppercase">引干</span>
                            <input
                              placeholder="引干"
                              value={formData.leadStem}
                              onChange={(e) => setFormData({ ...formData, leadStem: e.target.value })}
                              className="w-16 p-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-400 uppercase">空亡</span>
                            <ToggleSwitch checked={attributes.isVoid} onChange={() => setAttributes((prev) => ({ ...prev, isVoid: !prev.isVoid }))} />
                          </div>
                        </div>
                    </div>
                    
                    <button 
                        onClick={handleAnalyze} 
                        disabled={isLoading || !selectedImage} 
                        className="mt-6 w-full py-4 bg-fuchsia-700 hover:bg-fuchsia-800 disabled:bg-slate-300 text-white rounded-xl font-bold flex justify-center items-center gap-2 shadow-md"
                    >
                        {isLoading ? <Loader2 className="animate-spin w-5 h-5"/> : <Search className="w-5 h-5"/>} 
                        {isLoading ? "AI 正在分析全息物象..." : "开始定位"}
                    </button>
                </div>
            </div>

            <div className="lg:col-span-7">
                {result ? (
                    <div className="space-y-6 animate-fade-in-up">
                        <div className="bg-gradient-to-br from-indigo-50 to-white p-6 rounded-2xl border border-indigo-100 shadow-sm">
                            <h3 className="font-bold text-indigo-900 mb-2 flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-indigo-500"/> AI 象意聚焦综述
                            </h3>
                            <p className="text-sm text-indigo-800 leading-relaxed opacity-80">{result.analysis}</p>
                        </div>

                        <div className="space-y-4">
                            <h3 className="font-bold text-slate-700 px-1">最佳候选 ({result.objects?.length ?? 0})</h3>
                            {(result.objects ?? []).map((obj, i) => (
                                <div key={i} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
                                    <div className="absolute top-0 right-0 p-4 flex flex-col items-end">
                                        <div className={`text-xs font-bold px-2 py-1 rounded-full mb-1 ${getScoreColor(obj.similarity)}`}>
                                            相似度 {obj.similarity}%
                                        </div>
                                    </div>

                                    <div className="pr-20">
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 text-slate-600 font-bold text-sm border border-slate-200">
                                                {i + 1}
                                            </span>
                                            <h4 className="text-lg font-bold text-slate-900">{obj.objectName}</h4>
                                        </div>
                                        
                                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-3 bg-slate-50 w-fit px-2 py-1 rounded-md">
                                            <Target className="w-3 h-3"/>
                                            位置：{obj.location}
                                        </div>

                                        <p className="text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3 mt-2">
                                            <span className="font-bold text-slate-700">判定依据：</span>
                                            {obj.matchReason}
                                        </p>
                                    </div>

                                    <div className="absolute bottom-0 left-0 w-full h-1 bg-slate-100">
                                        <div 
                                            className={`h-full transition-all duration-1000 ease-out ${getScoreColor(obj.similarity).split(' ')[0]}`} 
                                            style={{ width: `${obj.similarity}%` }}
                                        ></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="h-full min-h-[400px] flex flex-col items-center justify-center bg-white rounded-3xl border-2 border-dashed border-slate-200 text-slate-400 p-8 text-center">
                        <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-6">
                            <ScanEye className="w-10 h-10 text-slate-300"/>
                        </div>
                        <h3 className="text-lg font-bold text-slate-600 mb-2">等待信号输入</h3>
                        <p className="max-w-xs text-sm">
                            请在左侧上传现场照片，并输入奇门盘中的宫位符号。AI 将自动计算视觉与象意的匹配度。
                        </p>
                        <div className="mt-8 flex gap-4 text-xs opacity-50">
                            <span className="flex items-center gap-1"><AlertCircle className="w-3 h-3"/> 自动识别</span>
                            <span className="flex items-center gap-1"><Target className="w-3 h-3"/> 精准定位</span>
                            <span className="flex items-center gap-1"><Sparkles className="w-3 h-3"/> 象意评分</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
      </div>
      {isLoading && (
        <AiTaskProgressOverlay
          title="AI 查找象意中……"
          etaLabel="35~45秒"
          progressPercent={loadingProgressPercent}
          steps={FIND_OBJECT_LOADING_STEPS}
          stepIndex={loadingStepIndex}
        />
      )}
    </div>
  );
};

export default FindObject;
